import { ChangeDetectionStrategy, Component, HostListener, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';

import { UiButtonComponent, UiCardComponent, UiSelectComponent, UiSelectOption } from '@shared/ui';

import { BreadcrumbItem, UiTopbarLanguage, UiTopbarUser } from './ui-topbar.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

interface UiTopbarNotification {
  id: string;
  title: string;
  description: string;
  meta: string;
  type: 'stock' | 'transfer' | 'approval' | 'task' | 'system';
  read: boolean;
  expanded: boolean;
}

@Component({
  selector: 'ui-topbar',
  standalone: true,
  imports: [MatToolbarModule, RouterLink, UiSelectComponent, UiButtonComponent, UiCardComponent, TranslatePipe, UiLabelTranslatePipe],
  templateUrl: './ui-topbar.component.html',
  styleUrls: ['./ui-topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTopbarComponent {
  private readonly notificationPageSize = 5;
  readonly breadcrumbs = input<BreadcrumbItem[]>([{ label: 'Dashboard', path: '/dashboard' }]);
  readonly user = input<UiTopbarUser>({
    name: 'Demo User',
    initials: 'DU',
    language: 'en'
  });

  readonly profileClicked = output<void>();
  readonly logoutClicked = output<void>();
  readonly languageChanged = output<UiTopbarLanguage>();
  readonly backClicked = output<void>();

  readonly menuOpen = signal(false);
  readonly notificationDrawerOpen = signal(false);
  readonly notificationLimit = signal(this.notificationPageSize);
  readonly notifications = signal<UiTopbarNotification[]>([
    {
      id: 'n-1',
      title: 'Low stock warning',
      description: 'Cimencam 42.5 stock is below threshold at Douala Central. Review reserve stock before the next transfer window.',
      meta: 'Inventory - 5 min ago',
      type: 'stock',
      read: false,
      expanded: false
    },
    {
      id: 'n-2',
      title: 'Transfer approved',
      description: 'Transfer TR-3034 from Central Warehouse to Yaounde Agency was approved and is ready for dispatch.',
      meta: 'Inventory - 12 min ago',
      type: 'transfer',
      read: false,
      expanded: false
    },
    {
      id: 'n-3',
      title: 'Fuel request pending',
      description: 'Logistics request FUEL-118 for vehicle LT-204 needs admin review with uploaded receipt proof.',
      meta: 'Logistics - 24 min ago',
      type: 'approval',
      read: false,
      expanded: false
    },
    {
      id: 'n-4',
      title: 'Agent task delayed',
      description: 'Agent route task AT-442 missed the planned POS check-in time for Bonamoussadi POS.',
      meta: 'Agents - 36 min ago',
      type: 'task',
      read: false,
      expanded: false
    },
    {
      id: 'n-5',
      title: 'Supervisor assignment updated',
      description: 'Supervisor Amina Diallo was assigned two additional agencies for this week.',
      meta: 'Supervisors - 48 min ago',
      type: 'system',
      read: true,
      expanded: false
    },
    {
      id: 'n-6',
      title: 'Banking note submitted',
      description: 'Banking note BN-209 from Douala Central is waiting for reconciliation review.',
      meta: 'Central Inventory - 1 hr ago',
      type: 'approval',
      read: true,
      expanded: false
    },
    {
      id: 'n-7',
      title: 'Campaign stock reserved',
      description: 'BTL campaign CP-074 reserved promotional stock for the weekend activation.',
      meta: 'BTL - 2 hrs ago',
      type: 'stock',
      read: false,
      expanded: false
    },
    {
      id: 'n-8',
      title: 'Vehicle document expiring',
      description: 'Vehicle LT-118 insurance document expires soon. Upload the renewed document before dispatch.',
      meta: 'Logistics - 3 hrs ago',
      type: 'system',
      read: true,
      expanded: false
    },
    {
      id: 'n-9',
      title: 'POS stock entry added',
      description: 'POS Bonaberi Kiosk received a new agent-entered stock update with proof attached.',
      meta: 'POS - 4 hrs ago',
      type: 'stock',
      read: false,
      expanded: false
    },
    {
      id: 'n-10',
      title: 'Role permission changed',
      description: 'A role permission update is now active for configuration access.',
      meta: 'Roles - Yesterday',
      type: 'system',
      read: true,
      expanded: false
    },
    {
      id: 'n-11',
      title: 'Maintenance approval needed',
      description: 'Maintenance request MAIN-221 includes new evidence photos and needs approval.',
      meta: 'Logistics - Yesterday',
      type: 'approval',
      read: false,
      expanded: false
    },
    {
      id: 'n-12',
      title: 'Agency target reached',
      description: 'Yaounde Agency reached 92% of its weekly direct-sales target.',
      meta: 'Agencies - Yesterday',
      type: 'transfer',
      read: true,
      expanded: false
    }
  ]);
  readonly unreadNotificationCount = computed(() => this.notifications().filter((item) => !item.read).length);
  readonly visibleNotifications = computed(() => this.notifications().slice(0, this.notificationLimit()));
  readonly hasMoreNotifications = computed(() => this.notificationLimit() < this.notifications().length);
  readonly languageOptions: UiSelectOption[] = [
    { label: 'English', value: 'en' },
    { label: 'French', value: 'fr' }
  ];

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  onProfileClicked(): void {
    this.profileClicked.emit();
    this.closeMenu();
  }

  onBackClicked(): void {
    this.backClicked.emit();
  }

  onLanguageChange(value: string): void {
    if (value === 'en' || value === 'fr') {
      this.languageChanged.emit(value);
    }
  }

  onLogoutClicked(): void {
    this.logoutClicked.emit();
    this.closeMenu();
  }

  toggleNotifications(): void {
    this.notificationDrawerOpen.set(!this.notificationDrawerOpen());
  }

  closeNotifications(): void {
    this.notificationDrawerOpen.set(false);
  }

  toggleNotification(id: string): void {
    // Clicking a notification marks it as read and expands/collapses details.
    this.notifications.update((items) => items.map((item) => (item.id === id ? { ...item, read: true, expanded: !item.expanded } : item)));
  }

  markAllAsRead(): void {
    // Keep "mark all" idempotent so reopening the drawer does not flip read messages back to unread.
    this.notifications.update((items) => items.map((item) => ({ ...item, read: true })));
  }

  clearNotifications(): void {
    this.notifications.set([]);
    this.notificationLimit.set(this.notificationPageSize);
  }

  onNotificationsScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

    if (reachedBottom && this.hasMoreNotifications()) {
      this.notificationLimit.update((limit) => Math.min(limit + this.notificationPageSize, this.notifications().length));
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }
}
